import { useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Building2 } from "lucide-react";

export default function Signup() {
  const { user, loading, applyAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ organisation_name: "", name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/signup", {
        organisation_name: form.organisation_name.trim(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      applyAuth(data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream-50">
      <div className="flex items-center justify-center px-6 md:px-12 py-12 animate-fade-up">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-8 w-8 rounded-md bg-forest-700 grid place-items-center">
              <span className="font-heading text-cream-50 text-sm font-semibold">3</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[15px] font-semibold text-forest-800">Sign-Off</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">Charity expense approvals</div>
            </div>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl text-forest-900 mb-2 tracking-tight">
            Create your organisation
          </h1>
          <p className="text-sm text-ink-soft mb-8">
            Set up your charity's workspace. You'll be the admin and can invite your team.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
            <Field label="Charity / organisation name" id="organisation_name">
              <Input id="organisation_name" required value={form.organisation_name} onChange={set("organisation_name")}
                data-testid="signup-org-input" placeholder="Concorde Community Trust"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
            </Field>
            <Field label="Your name" id="name">
              <Input id="name" required value={form.name} onChange={set("name")}
                data-testid="signup-name-input" placeholder="Jane Smith"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
            </Field>
            <Field label="Email" id="email">
              <Input id="email" type="email" required value={form.email} onChange={set("email")}
                data-testid="signup-email-input" placeholder="you@charity.org"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
            </Field>
            <Field label="Password (min 8 characters)" id="password">
              <Input id="password" type="password" required minLength={8} value={form.password} onChange={set("password")}
                data-testid="signup-password-input"
                className="bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700" />
            </Field>

            {error && (
              <div data-testid="signup-error-message"
                className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button type="submit" disabled={busy} data-testid="signup-submit-button"
              className="w-full bg-forest-700 hover:bg-forest-800 text-cream-50 h-11 font-medium">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create organisation"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-ink-soft">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="text-forest-700 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.pexels.com/photos/7475187/pexels-photo-7475187.jpeg" alt="Volunteers" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-forest-900/80 via-forest-800/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-cream-50">
          <Building2 className="h-7 w-7 mb-4 text-cream-200/90" />
          <h2 className="font-heading text-3xl xl:text-4xl leading-tight max-w-md">
            One workspace for your charity. Transparent sign-off, shared by your whole team.
          </h2>
        </div>
      </div>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-ink-soft">{label}</Label>
      {children}
    </div>
  );
}
